import {
    AfterViewInit,
    ChangeDetectorRef,
    Directive,
    ElementRef,
    EventEmitter,
    Injector,
    Input,
    OnDestroy,
    Output,
    Renderer2,
    SkipSelf,
} from '@angular/core';
import { NgForm, NgModel, ValidationErrors } from '@angular/forms';
import { FiComponentsContainer } from '@fi-shared/fi-components-container';
import { FiCustomerDataService } from '@fi-shared/fi-shared-services/fi-customer-data.service';
import { BehaviorSubject, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { ControlValueAccessorValidatorBase } from '@fi-shared/control-value-accessor-validator-base';

@Directive({
    selector: '[fiValidatingForm]',
    exportAs: 'fiValidatingForm',
    standalone: false,
})
export class FiValidatingFormDirective extends FiComponentsContainer implements AfterViewInit, OnDestroy {
    @Output('fiValidatingForm') onSave = new EventEmitter();
    @Output('fiValidatingFormError') onSaveError = new EventEmitter<NgModel[]>();

    @Input() disableDefaultScroll: boolean;
    @Input() scrollIntoViewOptions: ScrollIntoViewOptions = {};

    submitted = false;

    constructor(
        private elRef: ElementRef<HTMLFormElement>,
        private renderer: Renderer2,
        private ngForm: NgForm,
        private customerDataService: FiCustomerDataService,
        @SkipSelf() parentInjector: Injector,
    ) {
        super();

        const parentCdr = parentInjector.get(ChangeDetectorRef);
        this.errors$.pipe(takeUntil(this.ngOnDestroy$)).subscribe((s) => {
            parentCdr.markForCheck();
        });
    }

    ngAfterViewInit(): void {
        setTimeout(() => this.updateValidity(undefined));
    }

    submit(rootComponentsContainer?: FiComponentsContainer) {
        this.submitted = true;
        this.renderer.addClass(this.elRef.nativeElement, 'form-touched');

        const allInputs = (rootComponentsContainer ?? this).getDescendantNgModels();
        allInputs.forEach((item) => {
            item.control.markAsTouched();
            item.control.updateValueAndValidity();
        });

        this.getInputsWithErrors(allInputs).then((inputsWithErrors) => {
            // form validation finished

            //console.log('allInputs ', allInputs);
            //console.log('inputsWithErrors ', inputsWithErrors);

            this.setFormErrors(inputsWithErrors);
            console.log('inputsWithErrors', inputsWithErrors);

            if (inputsWithErrors.length) {
                this.onSaveError.emit(inputsWithErrors);
            } else {
                // todo after all changes this setTimeout should be redundant
                setTimeout(() => {
                    const elementsWithErrors = this.elRef.nativeElement.querySelectorAll('.ng-invalid');
                    if (elementsWithErrors.length) {
                        if (this.customerDataService.isTestCustomer()) {
                            window.alert(
                                'There are invalid components, not found by form, check if all parent components extend FiComponentsContainer or ControlValueAccessorValidatorBase',
                            );
                            console.log('not connected inputsWithErrors', elementsWithErrors);
                        }
                        this.onSaveError.emit([]);
                    } else {
                        this.onSave.emit();
                    }
                });
            }

            if (!this.disableDefaultScroll) {
                setTimeout(() => {
                    const elementsWithErrors = Array.from(this.elRef.nativeElement.querySelectorAll('.ng-invalid'));
                    if (elementsWithErrors.length) {
                        elementsWithErrors[0].scrollIntoView(
                            Object.assign(
                                {
                                    behavior: 'smooth',
                                },
                                this.scrollIntoViewOptions,
                            ),
                        );
                    }
                });
            }
        });
    }

    /* Check all child NgModels, gather their errors and update NgForm.errors with aggregated errors
       after executing this method form html element has relevant ng-valid/ng-invalid class
     */
    updateValidity(allInputs?: NgModel[]) {
        if (!allInputs) {
            allInputs = this.getDescendantNgModels();
        }

        this.getInputsWithErrors(allInputs).then((inputsWithErrors) => {
            this.setFormErrors(inputsWithErrors);
        });
    }

    private getInputsWithErrors(allInputs: NgModel[]) {
        return new Promise<NgModel[]>((resolve) => {
            setTimeout(() => {
                const asyncValidationPromises: Promise<void>[] = [];
                allInputs.forEach((item) => {
                    if (item.pending) {
                        this._pending$.next(true);
                        // async validators are working...
                        const asyncValidationPromise = new Promise<void>((resolve) => {
                            const sub = item.statusChanges.subscribe(() => {
                                if (!item.pending) {
                                    sub.unsubscribe();
                                    resolve();
                                }
                            });
                        });
                        asyncValidationPromises.push(asyncValidationPromise);
                    }
                });

                Promise.all(asyncValidationPromises).then(() => {
                    this._pending$.next(false);
                    // form validation finished
                    const inputsWithErrors = allInputs.filter((item) => !!item.errors);
                    resolve(inputsWithErrors);
                    inputsWithErrors.forEach((item) => {
                        if (item.valueAccessor instanceof ControlValueAccessorValidatorBase) {
                            //item.control.markAsTouched();
                            item.valueAccessor.markForCheck();
                        }
                    });
                });
            });
        });
    }

    private setFormErrors(inputsWithErrors: NgModel[]) {
        let aggregateErrors: ValidationErrors = null;
        if (!inputsWithErrors.length) {
            // form valid
            this.ngForm.form.setErrors(undefined);
        } else {
            // form invalid, gather errors from all children with errors
            aggregateErrors = {};
            inputsWithErrors.forEach((inputWithErrors) => {
                aggregateErrors = Object.assign(aggregateErrors, inputWithErrors.errors);
            });
            this.ngForm.form.setErrors(aggregateErrors);
        }
        this._errors$.next(this.ngForm.errors);
    }

    private _errors$ = new BehaviorSubject<ValidationErrors>(undefined);
    get errors$() {
        return this._errors$.asObservable();
    }
    get errors(): ValidationErrors {
        return this._errors$.value;
    }

    private _pending$ = new BehaviorSubject<boolean>(false);
    get pending$() {
        return this._pending$.asObservable();
    }
    get pending() {
        return this._pending$.value;
    }

    ngOnDestroy$: Subject<void> = new Subject<void>();
    ngOnDestroy() {
        this.ngOnDestroy$.next();
        this.ngOnDestroy$.complete();
    }
}
